import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE } from "../core/config.ts";
import { displayDpr } from "../utils/displayDpr.ts";

const LOGICAL_WIDTH = GRID_WIDTH * CELL_SIZE; // 960
const LOGICAL_HEIGHT = GRID_HEIGHT * CELL_SIZE; // 672

/**
 * Manages responsive canvas scaling while maintaining aspect ratio.
 * Scales the game canvas to fit the viewport with letterboxing.
 * Provides scale factor for input coordinate translation.
 */
export class CanvasScaler {
  private scale_ = 1;
  private offsetX_ = 0;
  private offsetY_ = 0;
  private dpr_ = 1;
  private canvases: HTMLCanvasElement[] = [];
  private wrapper: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  get scale(): number {
    return this.scale_;
  }

  get offsetX(): number {
    return this.offsetX_;
  }

  get offsetY(): number {
    return this.offsetY_;
  }

  get dpr(): number {
    return this.dpr_;
  }

  get logicalWidth(): number {
    return LOGICAL_WIDTH;
  }

  get logicalHeight(): number {
    return LOGICAL_HEIGHT;
  }

  attach(wrapper: HTMLElement, ...canvases: HTMLCanvasElement[]): void {
    this.wrapper = wrapper;
    this.canvases = canvases;
    this.dpr_ = displayDpr();

    // Observe the canvas-row (direct parent) for size changes
    const observeTarget = wrapper.parentElement ?? document.body;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeDebounceTimer !== null) {
        clearTimeout(this.resizeDebounceTimer);
      }
      this.resizeDebounceTimer = setTimeout(() => {
        this.resizeDebounceTimer = null;
        this.recalculate();
      }, 100);
    });
    this.resizeObserver.observe(observeTarget);

    window.addEventListener("resize", this.onResize);
    this.recalculate();
  }

  detach(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.resizeDebounceTimer !== null) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
    window.removeEventListener("resize", this.onResize);
  }

  /** Convert screen pixel coordinates to logical game coordinates. */
  screenToLogical(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this.offsetX_) / this.scale_,
      y: (screenY - this.offsetY_) / this.scale_,
    };
  }

  /** Get current scale factor for external use (e.g., pointer→cell mapping). */
  getScale(): number {
    return this.scale_;
  }

  private onResize = (): void => {
    if (this.resizeDebounceTimer !== null) {
      clearTimeout(this.resizeDebounceTimer);
    }
    this.resizeDebounceTimer = setTimeout(() => {
      this.resizeDebounceTimer = null;
      this.dpr_ = displayDpr();
      this.recalculate();
    }, 50);
  };

  private recalculate(): void {
    // Refresh DPR here so EVERY entry point (ResizeObserver, window resize,
    // attach) sizes the canvas with the same value renderPipeline reads live
    // via displayDpr() — otherwise a zoom change that fires only the
    // ResizeObserver leaves dpr_ stale and the logical-dims math drifts (F-007).
    this.dpr_ = displayDpr();
    const container = this.wrapper?.parentElement ?? document.body;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Calculate scale to fit while maintaining aspect ratio
    const scaleX = containerWidth / LOGICAL_WIDTH;
    const scaleY = containerHeight / LOGICAL_HEIGHT;
    const newScale = Math.min(scaleX, scaleY);

    // Calculate letterbox offsets for centering
    const displayWidth = Math.round(LOGICAL_WIDTH * newScale);
    const displayHeight = Math.round(LOGICAL_HEIGHT * newScale);

    // Early exit if nothing changed — prevents resize observer loops
    const newW = LOGICAL_WIDTH * this.dpr_;
    const newH = LOGICAL_HEIGHT * this.dpr_;
    if (
      this.scale_ === newScale &&
      this.canvases.length > 0 &&
      this.canvases[0]!.width === newW &&
      this.canvases[0]!.height === newH
    ) {
      return;
    }

    this.scale_ = newScale;
    this.offsetX_ = Math.floor((containerWidth - displayWidth) / 2);
    this.offsetY_ = Math.floor((containerHeight - displayHeight) / 2);

    // Apply to wrapper (sized and centered via flex parent)
    if (this.wrapper) {
      this.wrapper.style.width = `${displayWidth}px`;
      this.wrapper.style.height = `${displayHeight}px`;
    }

    // Apply to canvases (internal resolution = logical × DPR for sharp rendering)
    for (const canvas of this.canvases) {
      // Only set width/height when changed — setting clears the canvas
      const dimensionsChanged = canvas.width !== newW || canvas.height !== newH;
      if (dimensionsChanged) {
        canvas.width = newW;
        canvas.height = newH;
      }
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      // Scale 2D contexts for DPR (must re-apply after dimension change)
      if (dimensionsChanged) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.setTransform(this.dpr_, 0, 0, this.dpr_, 0, 0);
        }
      }
    }
  }
}

/** Compute scale and offsets for a given viewport (pure, for testing). */
export function computeScale(
  viewportWidth: number,
  viewportHeight: number,
): { scale: number; offsetX: number; offsetY: number } {
  const scaleX = viewportWidth / LOGICAL_WIDTH;
  const scaleY = viewportHeight / LOGICAL_HEIGHT;
  const scale = Math.min(scaleX, scaleY);
  const displayWidth = LOGICAL_WIDTH * scale;
  const displayHeight = LOGICAL_HEIGHT * scale;
  const offsetX = Math.floor((viewportWidth - displayWidth) / 2);
  const offsetY = Math.floor((viewportHeight - displayHeight) / 2);
  return { scale, offsetX, offsetY };
}
